import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Mail, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  bounced: "bg-amber-50 text-amber-700 border-amber-200",
  opened: "bg-cyan-50 text-cyan-700 border-cyan-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  unknown: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminEmailLogsPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/email-logs`, {
        params: { page, per_page: 30, status: status || undefined },
      });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load email logs");
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/exports/email-logs.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "psytech_email_logs.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-email-logs">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Logs</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total email events</p>
        </div>
        <div className="flex gap-2">
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="text-sm rounded-lg border border-slate-200 px-3 py-2 bg-white"
          >
            <option value="">All statuses</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="delivered">delivered</option>
            <option value="bounced">bounced</option>
          </select>
          <Button variant="outline" size="sm" onClick={onExport} data-testid="emails-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No email events yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Date</th>
                  <th className="px-4 py-2.5 font-semibold">To</th>
                  <th className="px-4 py-2.5 font-semibold">Subject</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Submission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                      {new Date(it.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-slate-800">{it.to_email}</td>
                    <td className="px-4 py-2.5 text-slate-700 max-w-[280px] truncate">{it.subject}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{it.submission_type || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_STYLES[it.status] || STATUS_STYLES.unknown}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {it.submission_id ? (
                        <Link to={`/admin/contact-requests/${it.submission_id}`} className="text-cyan-700 hover:text-cyan-900 text-xs">
                          open
                        </Link>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <div className="text-slate-500">Page {page} of {totalPages}</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
